/**
 * Fiscal Period Setup Step
 *
 * Configure fiscal periods during company setup with period type options
 */

import { useState, useEffect, useMemo } from 'react'

import { Checkbox } from '@elsapiens/ui'

import type { CompanySetupData } from '../../types/company-setup'

interface FiscalPeriodSetupStepProps {
  data: CompanySetupData
  onChange: (updates: Partial<CompanySetupData>) => void
  onNext: () => void
  onBack: () => void
}

export interface PeriodPreview {
  type: string
  name: string
  startDate: string
  endDate: string
}

export const PERIOD_TYPE_INFO = {
  monthly: {
    label: 'Monthly',
    description: 'Create 12 monthly periods for detailed tracking',
    count: 12,
  },
  quarterly: {
    label: 'Quarterly',
    description: 'Create 4 quarterly periods for standard reporting',
    count: 4,
  },
}

// =============================================================================
// Pure helpers (exported for testing)
// =============================================================================

export function formatPeriodDate(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

export function getLastDayOfMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate()
}

export function generateMonthlyPreviews(year: number, startMonth: number): PeriodPreview[] {
  const previews: PeriodPreview[] = []
  for (let i = 0; i < 12; i++) {
    const periodMonth = ((startMonth - 1 + i) % 12) + 1
    const periodYear = year + Math.floor((startMonth - 1 + i) / 12)
    previews.push({
      type: 'Monthly',
      name: `Month ${i + 1} - ${new Date(periodYear, periodMonth - 1).toLocaleString('default', { month: 'short' })} ${periodYear}`,
      startDate: formatPeriodDate(periodYear, periodMonth, 1),
      endDate: formatPeriodDate(periodYear, periodMonth, getLastDayOfMonth(periodYear, periodMonth)),
    })
  }
  return previews
}

export function generateQuarterlyPreviews(year: number, startMonth: number): PeriodPreview[] {
  const previews: PeriodPreview[] = []
  for (let i = 0; i < 4; i++) {
    const quarterStartMonth = ((startMonth - 1 + i * 3) % 12) + 1
    const quarterStartYear = year + Math.floor((startMonth - 1 + i * 3) / 12)
    const quarterEndMonth = ((startMonth - 1 + i * 3 + 2) % 12) + 1
    const quarterEndYear = year + Math.floor((startMonth - 1 + i * 3 + 2) / 12)
    previews.push({
      type: 'Quarterly',
      name: `Q${i + 1} FY${year}`,
      startDate: formatPeriodDate(quarterStartYear, quarterStartMonth, 1),
      endDate: formatPeriodDate(quarterEndYear, quarterEndMonth, getLastDayOfMonth(quarterEndYear, quarterEndMonth)),
    })
  }
  return previews
}

// =============================================================================
// Sub-components
// =============================================================================

interface PeriodPreviewTableProps {
  previews: PeriodPreview[]
}

function PeriodPreviewTable({ previews }: PeriodPreviewTableProps): JSX.Element {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="bg-surface-secondary px-4 py-3 border-b border-border">
        <h3 className="text-sm font-medium text-foreground">
          Period Preview ({previews.length} periods)
        </h3>
      </div>
      <div className="max-h-64 overflow-y-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-secondary sticky top-0">
            <tr>
              {['Type', 'Period Name', 'Start Date', 'End Date'].map((h) => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border">
            {previews.map((period, index) => (
              <tr key={index} className="hover:bg-surface-secondary">
                <td className="px-4 py-2 text-sm text-foreground">
                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                    {period.type}
                  </span>
                </td>
                <td className="px-4 py-2 text-sm text-foreground">{period.name}</td>
                <td className="px-4 py-2 text-sm text-foreground-secondary">
                  {new Date(period.startDate.includes('T') ? period.startDate : `${period.startDate}T00:00:00`).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 text-sm text-foreground-secondary">
                  {new Date(period.endDate.includes('T') ? period.endDate : `${period.endDate}T00:00:00`).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface PeriodSummaryBannerProps {
  totalPeriods: number
  showPreview: boolean
  onTogglePreview: () => void
}

function PeriodSummaryBanner({ totalPeriods, showPreview, onTogglePreview }: PeriodSummaryBannerProps): JSX.Element {
  return (
    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" fillRule="evenodd" />
        </svg>
        <div>
          <p className="text-sm font-medium text-green-900 dark:text-green-100">
            {totalPeriods} fiscal period{totalPeriods !== 1 ? 's' : ''} will be created
          </p>
          <button
            className="text-sm text-green-700 dark:text-green-300 mt-1 underline hover:no-underline"
            type="button"
            onClick={onTogglePreview}
          >
            {showPreview ? 'Hide' : 'Show'} preview
          </button>
        </div>
      </div>
    </div>
  )
}

function NoAutoCreateWarning(): JSX.Element {
  return (
    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path clipRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" fillRule="evenodd" />
        </svg>
        <div>
          <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
            Fiscal periods are required
          </p>
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
            You&apos;ll need to create fiscal periods manually from the Fiscal Periods page before you can post journal entries. We recommend enabling auto-creation to get started quickly.
          </p>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Component
// =============================================================================

type FiscalPeriodSetup = NonNullable<FiscalPeriodSetupStepProps['data']['fiscalPeriodSetup']>

function useFiscalPeriodSetup(data: FiscalPeriodSetupStepProps['data'], onChange: FiscalPeriodSetupStepProps['onChange']): { fiscalPeriodSetup: FiscalPeriodSetup; fiscalYearStartMonth: number; currentYear: number } {
  const fiscalYearStartMonth = useMemo(() => {
    if (!data.companyInfo.fiscalYearStart) { return 1 }
    const date = new Date(data.companyInfo.fiscalYearStart)
    return date.getMonth() + 1
  }, [data.companyInfo.fiscalYearStart])

  const currentYear = new Date().getFullYear()

  useEffect(() => {
    if (!data.fiscalPeriodSetup) {
      onChange({
        fiscalPeriodSetup: {
          createInitialYear: true,
          initialFiscalYear: currentYear,
          periodTypes: { monthly: true, quarterly: false },
          startMonth: fiscalYearStartMonth,
        },
      })
    } else if (data.fiscalPeriodSetup.startMonth !== fiscalYearStartMonth) {
      onChange({
        fiscalPeriodSetup: { ...data.fiscalPeriodSetup, startMonth: fiscalYearStartMonth },
      })
    }
  }, [fiscalYearStartMonth, data.fiscalPeriodSetup, onChange, currentYear])

  const fiscalPeriodSetup = useMemo(() => data.fiscalPeriodSetup || {
    createInitialYear: true,
    initialFiscalYear: currentYear,
    periodTypes: { monthly: true, quarterly: false },
    startMonth: fiscalYearStartMonth,
  }, [data.fiscalPeriodSetup, currentYear, fiscalYearStartMonth])

  return { fiscalPeriodSetup, fiscalYearStartMonth, currentYear }
}

function usePeriodPreviews(fiscalPeriodSetup: FiscalPeriodSetup, hasSelectedPeriodType: boolean): PeriodPreview[] {
  return useMemo((): PeriodPreview[] => {
    if (!fiscalPeriodSetup.createInitialYear || !hasSelectedPeriodType) { return [] }
    const previews: PeriodPreview[] = []
    const { initialFiscalYear: year, startMonth } = fiscalPeriodSetup
    if (fiscalPeriodSetup.periodTypes.monthly) {
      previews.push(...generateMonthlyPreviews(year, startMonth))
    }
    if (fiscalPeriodSetup.periodTypes.quarterly) {
      previews.push(...generateQuarterlyPreviews(year, startMonth))
    }
    return previews
  }, [fiscalPeriodSetup, hasSelectedPeriodType])
}

interface FiscalDerivedState {
  totalPeriods: number
  hasSelectedPeriodType: boolean
  canProceed: boolean
  updateFiscalPeriodSetup: (updates: Partial<FiscalPeriodSetup>) => void
  togglePeriodType: (type: keyof FiscalPeriodSetup['periodTypes']) => void
  periodPreviews: PeriodPreview[]
}

function useFiscalDerivedState(fiscalPeriodSetup: FiscalPeriodSetup, onChange: FiscalPeriodSetupStepProps['onChange']): FiscalDerivedState {
  const totalPeriods = useMemo(() => {
    let count = 0
    if (fiscalPeriodSetup.periodTypes.monthly) { count += 12 }
    if (fiscalPeriodSetup.periodTypes.quarterly) { count += 4 }
    return count
  }, [fiscalPeriodSetup.periodTypes])

  const hasSelectedPeriodType = useMemo(
    () => Object.values(fiscalPeriodSetup.periodTypes).some((v) => v),
    [fiscalPeriodSetup.periodTypes],
  )

  const canProceed = !fiscalPeriodSetup.createInitialYear || (hasSelectedPeriodType && fiscalPeriodSetup.initialFiscalYear > 0)

  const updateFiscalPeriodSetup = (updates: Partial<typeof fiscalPeriodSetup>): void => {
    onChange({ fiscalPeriodSetup: { ...fiscalPeriodSetup, ...updates } })
  }

  const togglePeriodType = (type: keyof typeof fiscalPeriodSetup.periodTypes): void => {
    updateFiscalPeriodSetup({
      periodTypes: { ...fiscalPeriodSetup.periodTypes, [type]: !fiscalPeriodSetup.periodTypes[type] },
    })
  }

  const periodPreviews = usePeriodPreviews(fiscalPeriodSetup, hasSelectedPeriodType)
  return { totalPeriods, hasSelectedPeriodType, canProceed, updateFiscalPeriodSetup, togglePeriodType, periodPreviews }
}

export function FiscalPeriodSetupStep({
  data,
  onChange,
  onNext,
  onBack,
}: FiscalPeriodSetupStepProps): JSX.Element {
  const [shouldShowPreview, setShouldShowPreview] = useState(false)
  const { fiscalPeriodSetup, currentYear } = useFiscalPeriodSetup(data, onChange)
  const { totalPeriods, hasSelectedPeriodType, canProceed, updateFiscalPeriodSetup, togglePeriodType, periodPreviews } =
    useFiscalDerivedState(fiscalPeriodSetup, onChange)

  return (
    <div className="space-y-6">
      <div className="p-4 border border-border rounded-lg">
        <Checkbox
          hideMessage
          checked={fiscalPeriodSetup.createInitialYear}
          description="Automatically create fiscal periods based on your fiscal year dates. You can customize period types below."
          label={`Auto-create fiscal periods for ${fiscalPeriodSetup.initialFiscalYear}`}
          onChange={(checked: boolean): void => updateFiscalPeriodSetup({ createInitialYear: checked })}
        />
      </div>
      {fiscalPeriodSetup.createInitialYear && (
        <CreateYearSection
          currentYear={currentYear}
          fiscalPeriodSetup={fiscalPeriodSetup}
          fiscalYearStart={data.companyInfo.fiscalYearStart}
          hasSelectedPeriodType={hasSelectedPeriodType}
          periodPreviews={periodPreviews}
          shouldShowPreview={shouldShowPreview}
          togglePeriodType={togglePeriodType}
          totalPeriods={totalPeriods}
          updateFiscalPeriodSetup={updateFiscalPeriodSetup}
          onTogglePreview={() => setShouldShowPreview(!shouldShowPreview)}
        />
      )}
      {!fiscalPeriodSetup.createInitialYear && <NoAutoCreateWarning />}
      <FiscalActionBar canProceed={canProceed} onBack={onBack} onNext={onNext} />
    </div>
  )
}

interface CreateYearSectionProps {
  fiscalPeriodSetup: FiscalPeriodSetup
  currentYear: number
  fiscalYearStart: string
  hasSelectedPeriodType: boolean
  shouldShowPreview: boolean
  totalPeriods: number
  periodPreviews: PeriodPreview[]
  togglePeriodType: (type: keyof FiscalPeriodSetup['periodTypes']) => void
  updateFiscalPeriodSetup: (updates: Partial<FiscalPeriodSetup>) => void
  onTogglePreview: () => void
}

function CreateYearSection(p: CreateYearSectionProps): JSX.Element {
  return (
    <>
      <FiscalYearInput
        currentYear={p.currentYear}
        fiscalPeriodSetup={p.fiscalPeriodSetup}
        fiscalYearStart={p.fiscalYearStart}
        updateFiscalPeriodSetup={p.updateFiscalPeriodSetup}
      />
      <PeriodTypeFieldset
        fiscalPeriodSetup={p.fiscalPeriodSetup}
        hasSelectedPeriodType={p.hasSelectedPeriodType}
        togglePeriodType={p.togglePeriodType}
      />
      {p.hasSelectedPeriodType && (
        <PeriodSummaryBanner
          showPreview={p.shouldShowPreview}
          totalPeriods={p.totalPeriods}
          onTogglePreview={p.onTogglePreview}
        />
      )}
      {p.shouldShowPreview && p.periodPreviews.length > 0 && (
        <PeriodPreviewTable previews={p.periodPreviews} />
      )}
    </>
  )
}

interface FiscalYearInputProps {
  fiscalPeriodSetup: FiscalPeriodSetup
  currentYear: number
  fiscalYearStart: string
  updateFiscalPeriodSetup: (updates: Partial<FiscalPeriodSetup>) => void
}

function FiscalYearInput({ fiscalPeriodSetup, currentYear, fiscalYearStart, updateFiscalPeriodSetup }: FiscalYearInputProps): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="fiscal-year">
        Fiscal Year
      </label>
      <input
        className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring bg-surface text-foreground"
        id="fiscal-year"
        max={currentYear + 5}
        min={currentYear - 5}
        type="number"
        value={fiscalPeriodSetup.initialFiscalYear}
        onChange={(e): void => updateFiscalPeriodSetup({ initialFiscalYear: parseInt(e.target.value) || currentYear })}
      />
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
        Your fiscal year starts on {new Date(fiscalYearStart).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
      </p>
    </div>
  )
}

interface PeriodTypeFieldsetProps {
  fiscalPeriodSetup: FiscalPeriodSetup
  hasSelectedPeriodType: boolean
  togglePeriodType: (type: keyof FiscalPeriodSetup['periodTypes']) => void
}

function PeriodTypeFieldset({ fiscalPeriodSetup, hasSelectedPeriodType, togglePeriodType }: PeriodTypeFieldsetProps): JSX.Element {
  return (
    <fieldset>
      <legend className="block text-sm font-medium text-foreground-secondary mb-3">
        Select Period Types to Create
      </legend>
      <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
        {(Object.keys(PERIOD_TYPE_INFO) as Array<keyof typeof PERIOD_TYPE_INFO>).map((type) => (
          <div
            key={type}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              fiscalPeriodSetup.periodTypes[type]
                ? 'border-primary bg-muted'
                : 'border-border hover:border-primary'
            }`}
          >
            <Checkbox
              hideMessage
              checked={fiscalPeriodSetup.periodTypes[type]}
              description={`${PERIOD_TYPE_INFO[type].description} (${PERIOD_TYPE_INFO[type].count} period${PERIOD_TYPE_INFO[type].count !== 1 ? 's' : ''} per year)`}
              label={PERIOD_TYPE_INFO[type].label}
              onChange={(): void => togglePeriodType(type)}
            />
          </div>
        ))}
      </div>
      {!hasSelectedPeriodType && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
          Please select at least one period type
        </p>
      )}
    </fieldset>
  )
}

function FiscalActionBar({ canProceed, onBack, onNext }: { canProceed: boolean; onBack: () => void; onNext: () => void }): JSX.Element {
  return (
    <div className="flex justify-between pt-4">
      <button
        className="px-3 py-2 border border-border text-foreground-secondary rounded-lg hover:bg-surface-secondary transition-colors font-medium"
        type="button"
        onClick={onBack}
      >
        Back
      </button>
      <button
        className="px-3 py-2 bg-primary-solid text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        disabled={!canProceed}
        type="button"
        onClick={onNext}
      >
        Next: Bank Accounts
      </button>
    </div>
  )
}
